import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { format } from 'date-fns'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiSearch, FiTrendingUp, FiZap, FiMail, FiChevronLeft, FiChevronRight } = FiIcons

const CreditHistory = ({ transactions = [], currentPage = 1, totalPages = 1, onPageChange }) => {
  // Helper function to display the correct amount
  const displayAmount = (transaction) => {
    // For content generation and email extraction, always show -0.0025 regardless of what's stored
    if (transaction.description === 'content_generation' || transaction.description === 'email_extraction') {
      return '-0.0025'
    }
    // For broad and custom searches, normalize view to the new fixed costs
    if (transaction.description === 'broad_search') {
      return `-${(0.5).toFixed(4)}`
    }
    if (transaction.description === 'custom_search') {
      return `-${(0.1).toFixed(4)}`
    }
    // For other transactions, format to 4 decimal places
    return Number(transaction.amount).toFixed(4)
  }

  // Helper function to format transaction display
  const formatTransactionDisplay = (transaction) => {
    if (transaction.transaction_type === 'purchase') {
      return `Purchased ${transaction.amount} credits`
    }
    if (transaction.transaction_type === 'usage') {
      if (transaction.description === 'content_generation') {
        return 'Used 0.0025 credits for content generation'
      }
      if (transaction.description === 'email_extraction') {
        return 'Used 0.0025 credits for email extraction'
      }
      if (transaction.description === 'broad_search') {
        return 'Used 0.5 credits for broad search'
      }
      if (transaction.description === 'custom_search') {
        return 'Used 0.1 credits for custom search'
      }
      return `Used ${Math.abs(transaction.amount)} credits`
    }
    if (transaction.transaction_type === 'bonus') {
      return transaction.description || 'Bonus credits'
    }
    return 'Credit transaction'
  }

  console.log("Rendering CreditHistory with transactions:", transactions)

  if (!transactions || transactions.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 dark:bg-gray-800">
        <h2 className="text-xl font-semibold mb-4 dark:text-white">Credit History</h2>
        <p className="text-gray-600 text-center py-6 dark:text-gray-400">No transactions yet</p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 dark:bg-gray-800">
      <h2 className="text-xl font-semibold mb-4 dark:text-white">Credit History</h2>
      <div className="space-y-3">
        {transactions.map((transaction) => (
          <motion.div
            key={transaction.id || `transaction-${Math.random()}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg dark:bg-gray-700"
          >
            <div className="flex items-center">
              <div className={`p-2 rounded-lg mr-3 ${
                transaction.transaction_type === 'purchase' 
                  ? 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400' 
                  : 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400'
              }`}>
                <SafeIcon
                  icon={
                    transaction.transaction_type === 'purchase'
                      ? FiTrendingUp
                      : transaction.description === 'content_generation'
                      ? FiZap
                      : transaction.description === 'email_extraction'
                      ? FiMail
                      : FiSearch
                  }
                  className="w-4 h-4"
                />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  {formatTransactionDisplay(transaction)}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {transaction.created_at ? format(new Date(transaction.created_at), 'MMM d, yyyy HH:mm') : 'Date unavailable'}
                </p>
              </div>
            </div>
            <div className={`text-lg font-semibold ${
              transaction.amount > 0 
                ? 'text-green-600 dark:text-green-400' 
                : 'text-red-600 dark:text-red-400'
            }`}>
              {transaction.amount > 0 ? '+' : ''}
              {displayAmount(transaction)}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6 pt-4 border-t dark:border-gray-700">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed dark:text-gray-400 dark:hover:text-gray-200"
            >
              <SafeIcon icon={FiChevronLeft} className="w-4 h-4 mr-1" />
              Previous
            </button>
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed dark:text-gray-400 dark:hover:text-gray-200"
            >
              Next
              <SafeIcon icon={FiChevronRight} className="w-4 h-4 ml-1" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default CreditHistory